import { useHandler } from "@redotech/react-util/hook";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import {
  ActionStepType,
  actionStepTypes,
  actionStepTypeToFriendlyName,
  SkipCondition,
  TriggerStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { Category } from "@redotech/redo-flows-common/triggers";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import { LabelText } from "@redotech/redo-web/arbiter-components/common/descriptors";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { lowercaseFirstLetter } from "@redotech/string/transform";
import { ObjectId } from "bson";
import { memo, useMemo } from "react";
import {
  ConditionSummarySegment,
  getConditionSummary,
} from "../../nodes/condition-summary";
import { useConditionValueLabels } from "../../nodes/condition-value-labels";
import {
  DeepPartial,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../../steps";
import { ConditionExpressionInspector, expressionIsValid } from "../condition";
import {
  TriggerConditionCard,
  TriggerConditionsSection,
} from "./trigger-conditions-section";

type ConditionExpressionType = NonNullable<
  TriggerStep["skipConditions"]
>["conditions"][number];

export const SkipConditionsInspector = memo(function SkipConditionsInspector({
  step,
  setStep,
  schema,
  category,
}: StepTypeDetailsProps<PartialWithRequiredType<TriggerStep>>) {
  const { t } = useTranslation();
  const addNewCondition = useHandler(() => {
    setStep({
      ...step,
      skipConditions: {
        conjunctionMode: ConjunctionMode.OR,
        ...step.skipConditions,
        conditions: [
          ...(step.skipConditions?.conditions ?? []),
          {
            dataSource: undefined,
            actionTypes: actionStepTypes,
            _id: new ObjectId().toString(),
          },
        ],
      },
    });
  });

  const setConjunctionMode = useHandler((conjunctionMode: ConjunctionMode) => {
    setStep({
      ...step,
      skipConditions: {
        conditions: [],
        ...step.skipConditions,
        conjunctionMode,
      },
    });
  });

  const removeCondition = useHandler((index: number) => {
    const newConditions = step.skipConditions?.conditions ?? [];
    newConditions.splice(index, 1);
    setStep({
      ...step,
      skipConditions: { ...step.skipConditions, conditions: newConditions },
    });
  });

  return (
    <TriggerConditionsSection
      addButtonText={t("Skip condition")}
      addCondition={addNewCondition}
      conditionCount={step.skipConditions?.conditions?.length ?? 0}
      conjunctionMode={
        step.skipConditions?.conjunctionMode ?? ConjunctionMode.OR
      }
      conjunctionText={t(
        "of these conditions must be met to skip an action step",
      )}
      setConjunctionMode={setConjunctionMode}
      singleConditionText={t("Skip action steps when any condition is met")}
      title={t("Skip conditions")}
    >
      {step.skipConditions?.conditions?.map((condition, index) => {
        return (
          <SkipConditionBlock
            category={category}
            condition={condition}
            key={condition._id}
            removeCondition={() => removeCondition(index)}
            schema={schema}
            setCondition={(newCondition) => {
              const newConditions = step.skipConditions?.conditions ?? [];
              newConditions.splice(index, 1, newCondition);
              setStep({
                ...step,
                skipConditions: {
                  ...step.skipConditions,
                  conditions: newConditions,
                },
              });
            }}
          />
        );
      })}
    </TriggerConditionsSection>
  );
});

const SkipConditionBlock = memo(function SkipConditionBlock({
  condition,
  setCondition,
  removeCondition,
  schema,
  category,
}: {
  condition: DeepPartial<SkipCondition>;
  setCondition: (condition: DeepPartial<SkipCondition>) => void;
  removeCondition: () => void;
  schema: Schema;
  category: Category;
}) {
  const { t } = useTranslation();
  const actionTypeSummary = useMemo(() => {
    if (
      !condition.actionTypes ||
      actionStepTypes.every((type) => condition.actionTypes?.includes(type))
    ) {
      return t("all actions");
    }
    return condition.actionTypes
      .map((type) => lowercaseFirstLetter(actionStepTypeToFriendlyName[type]))
      .join(", ");
  }, [condition.actionTypes, t]);

  const valueLabels = useConditionValueLabels(condition);

  const conditionSummary = useMemo(
    (): ConditionSummarySegment[] => [
      {
        kind: "text",
        text: t("Skips {{actionType}} when", { actionType: actionTypeSummary }),
      },
      ...getConditionSummary(condition, valueLabels, schema),
    ],
    [actionTypeSummary, condition, valueLabels, schema, t],
  );

  const actionTypeOptions: BadgeRedoListItem<ActionStepType>[] = useMemo(() => {
    return actionStepTypes.map((type) => ({
      value: type,
      id: type,
      type: "badge",
      badge: { text: actionStepTypeToFriendlyName[type] },
    }));
  }, []);

  return (
    <TriggerConditionCard
      isValid={expressionIsValid(condition)}
      label={t("Skip condition")}
      removeCondition={removeCondition}
      summary={conditionSummary}
    >
      <LabelText label={t("Action type")} size="xs" />
      <RedoTagsDropdownInput<ActionStepType>
        options={actionTypeOptions}
        placeholder={t("Select action types")}
        selectedOptions={actionTypeOptions.filter((o) =>
          condition?.actionTypes?.includes(o.value),
        )}
        setSelectedOptions={(options) => {
          setCondition({
            ...condition,
            actionTypes: options.map((o) => o.value),
          });
        }}
        size="xs"
      />
      <ConditionExpressionInspector
        category={category}
        expression={condition}
        schema={schema}
        setExpression={(newExpression) => {
          setCondition(
            {
              _id: condition._id,
              actionTypes: condition.actionTypes,
              ...newExpression,
            } as ConditionExpressionType, // Recursive array-match types conflict here.
          );
        }}
        showTitle={false}
      />
      <Divider />
    </TriggerConditionCard>
  );
});
